import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ScanFace, Search, X, Check, Link2, Link2Off, Loader2 } from "lucide-react";
import { useI18n } from "i18n";
import api from "lib/api";
import FaceEnrollButton from "components/FaceEnrollButton";

// Account edit screen — facial biometrics for one login account (super admin):
//  • generate a one-time enrollment link (new capture), OR
//  • link the account to an EXISTING biometric person (reuse its template),
//  • unlink.
export default function AccountFaceBiometrics({ userId }) {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);   // {enrolled, person_id, person_name}
  const [pick, setPick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get(`/api/auth/face/status/?user_id=${encodeURIComponent(userId)}`)
      .then((r) => setStatus(r.data)).catch(() => setStatus(null));
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  const linkTo = async (personId) => {
    setBusy(true); setErr("");
    try {
      await api.post("/api/auth/face/link/", { user_id: userId, person_id: personId });
      setPick(false); load();
    } catch (e) {
      setErr(e.response?.data?.detail || t("face.link.failed", "Falha ao vincular."));
    } finally { setBusy(false); }
  };
  const unlink = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/api/auth/face/link/", { user_id: userId, action: "unlink" });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || t("face.link.failed", "Falha ao desvincular."));
    } finally { setBusy(false); }
  };

  const linked = status && status.person_id;

  return (
    <div>
      {/* Current state */}
      <div className="mb-4 text-sm">
        {linked ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">{t("face.link.linkedTo", "Vinculado a")}:</span>
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1">
              <ScanFace className="h-3.5 w-3.5 text-primary" />
              <b>{status.person_name || status.person_id.slice(0, 8)}</b>
              {status.enrolled
                ? <span className="inline-flex items-center gap-0.5 text-emerald-500 text-xs"><Check className="h-3 w-3" />{t("face.link.enrolled", "cadastrado")}</span>
                : <span className="text-amber-500 text-xs">{t("face.link.notEnrolled", "sem template")}</span>}
            </span>
            <button type="button" onClick={unlink} disabled={busy}
              className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline">
              <Link2Off className="h-3.5 w-3.5" /> {t("face.link.unlink", "Desvincular")}
            </button>
          </div>
        ) : (
          <span className="text-muted-foreground">{t("face.link.none", "Nenhuma biometria vinculada a esta conta.")}</span>
        )}
      </div>

      {err && <p className="text-sm text-red-500 mb-3">{err}</p>}

      <div className="flex flex-wrap gap-2">
        <FaceEnrollButton targetUserId={userId} />
        <button type="button" onClick={() => setPick(true)}
          className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-border text-sm hover:bg-muted transition-colors">
          <Link2 className="h-4 w-4" /> {t("face.link.pickCta", "Vincular a biometria existente")}
        </button>
      </div>

      {pick && createPortal(
        <PickerModal t={t} onClose={() => setPick(false)} onPick={linkTo} busy={busy} />, document.body)}
    </div>
  );
}

function PickerModal({ t, onClose, onPick, busy }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const search = useCallback((query) => {
    setLoading(true);
    api.get(`/api/biometric-people/?q=${encodeURIComponent(query || "")}`)
      .then((r) => setRows(r.data.results || [])).catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { search(""); }, [search]);

  return (
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-start justify-center p-4 overflow-auto">
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-xl my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2"><Link2 className="h-5 w-5 text-primary" />
            <h2 className="font-semibold">{t("face.link.pickTitle", "Vincular a biometria existente")}</h2></div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); search(e.target.value); }}
              placeholder={t("face.link.searchPh", "Buscar por nome ou identidade…")}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-border bg-background text-sm" />
          </div>
          <div className="max-h-[50vh] overflow-auto divide-y divide-border rounded-lg border border-border">
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
            ) : rows.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">{t("face.link.noResults", "Nenhuma pessoa encontrada.")}</p>
            ) : rows.map((p) => (
              <button key={p.id} type="button" disabled={busy} onClick={() => onPick(p.id)}
                className="w-full text-left px-4 py-3 hover:bg-muted/40 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name || p.id.slice(0, 8)}</div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(p.identities || []).slice(0, 4).map((i, k) => (
                      <span key={k} className="text-[11px] text-muted-foreground border border-border rounded px-1.5 py-0.5">{i.type}:{i.value}</span>
                    ))}
                  </div>
                </div>
                {p.enrolled
                  ? <span className="shrink-0 inline-flex items-center gap-0.5 text-emerald-500 text-xs"><Check className="h-3 w-3" />{t("face.link.enrolled", "cadastrado")}</span>
                  : <span className="shrink-0 text-amber-500 text-xs">{t("face.link.notEnrolled", "sem template")}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
